<template>
    <Card
        :clickable="!!(courseLink || $attrs.to)"
        :href="courseLink"
        v-bind="$attrs"
        :title="courseName"
        class="card-course"
    >
        <template
            v-if="uniName"
            #contentArea
        >
            <component
                :is="uniLink ? 'link' : 'span'"
                :href="uniLink"
                :class="[
                    'uni-link',
                    {
                        'text-primary': uniLink,
                    },
                ]"
                @navigate.stop
            >
                {{ uniName }}
            </component>
        </template>

        <template
            v-if="hasStats"
            #secondaryActionArea
        >
            <TagList
                :tags="tags"
                tag-bare
                tag-size="sm"
            />
        </template>
    </Card>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue';
import Card from '@/components/Card';
import { getCourseLink, getUniLink } from '@/router/links';
import { onlyTruthy } from '@/helpers/array';
import { provideIsDashboardKey } from '@/components/keys';

export default defineComponent({
    name: 'CardCourse',
    components: {
        Card,
    },
    inheritAttrs: false,
    props: {
        /**
         * Name of the course
         */
        courseName: {
            type: String,
            required: true,
        },
        /**
         * When specified, the card can provide navigation to the course page
         */
        courseId: {
            type: Number,
            default: undefined,
        },
        /**
         * Name of the uni the course belongs to.
         */
        uniName: {
            type: String,
            default: undefined,
        },
        /**
         * When specified, the uni will link to the uni page
         */
        uniId: {
            type: Number,
            default: undefined,
        },
        /**
         * Number of posts in the course
         */
        numPosts: {
            type: Number,
            default: undefined,
        },
        /**
         * Number of documents uploaded to the course
         */
        numDocuments: {
            type: Number,
            default: undefined,
        },
        /**
         * Number of people that joined the course
         */
        numUsers: {
            type: Number,
            default: undefined,
        },
        /**
         * Enables clickable uni link
         */
        enableLinks: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        const isDashboard = inject(provideIsDashboardKey, null); // Default to null if not provided

        return {
            isDashboard,
        };
    },
    computed: {
        courseLink() {
            return (
                (this.$attrs.href as string | undefined) ||
                (this.courseId && getCourseLink(this.courseId, this.courseName, this.isDashboard)) ||
                undefined
            );
        },
        uniLink() {
            return (
                (this.enableLinks && this.uniId && this.uniName && getUniLink(this.uniId, this.uniName)) || undefined
            );
        },
        hasStats() {
            return this.numPosts !== undefined || this.numDocuments !== undefined || this.numUsers !== undefined;
        },
        tags() {
            return onlyTruthy([
                this.numPosts !== undefined && {
                    icon: 'chat',
                    value: this.numPosts,
                },
                this.numDocuments !== undefined && {
                    icon: 'documents',
                    value: this.numDocuments,
                },
                this.numUsers !== undefined && {
                    icon: 'users',
                    value: this.numUsers,
                },
            ]);
        },
    },
});
</script>
